import {
  OrderSide,
  assetUnitsToDecimal,
  calculateGrossFillQuantities,
  decimalToPip,
  dividePips,
  oneInPips,
  determineMaximumReduceOnlyQuantityAvailableAtPriceLevel,
  pipToDecimal,
} from '@katanaperps/katana-perps-sdk';

import {
  ApiLevelTwoOrderBookRow,
  ApiMarket,
  ApiOrder,
  ApiPosition,
} from 'api/types';
import { EXCHANGE_DECIMALS } from 'config/constants';
import { BigNumberClass, BigNumberJs } from 'lib/BigNumber';
import { formatNumber } from 'lib/display/formatting';

import { PanelState } from './buyPanelReducer';

const calculateImpact = (
  quantity: string,
  quantityInQuote: boolean,
  limitPrice: string | undefined,
  stepSizeDecimals: number,
  list: [price: string, size: string, _: unknown][],
  side: OrderSide
) => {
  const quantities2p = calculateGrossFillQuantities(
    list
      // The provided list of orders or price levels (asks or bids) is expected to be sorted by best price
      // (ascending for asks (lowest first), descending for bids (highest first)). Multiple orders per price level are supported.
      .map(askOrBid => ({
        price: decimalToPip(askOrBid[0]),
        size: decimalToPip(askOrBid[1]),
      }))
      .values(),
    {
      isQuantityInQuote: quantityInQuote,
      quantity: decimalToPip(quantity),
      side,
      limitPrice: limitPrice ? decimalToPip(limitPrice) : undefined,
    },
    true // double pip precision
  );
  const baseQuantityBN = BigNumberJs(
    quantities2p.baseQuantity.toString()
  ).dividedBy(10 ** (EXCHANGE_DECIMALS * 2));
  const baseQuantityFormatted = baseQuantityBN.toFixed(
    stepSizeDecimals,
    BigNumberClass.ROUND_DOWN
  );

  const quoteQuantityFormatted = BigNumberJs(
    quantities2p.quoteQuantity.toString()
  )
    .dividedBy(10 ** (EXCHANGE_DECIMALS * 2))
    .toFixed(8);

  const impactPrice2p = dividePips(
    quantities2p.quoteQuantity * oneInPips,
    quantities2p.baseQuantity
  );
  const impactPrice = assetUnitsToDecimal(impactPrice2p, EXCHANGE_DECIMALS * 2);
  return {
    impactPrice2p,
    impactPrice,
    quantities2p,
    baseQuantityBN,
    baseQuantityFormatted,
    quoteQuantityFormatted,
  };
};

/**
 * Calculate limit or market order executed quantities
 * Note: For limit orders placed on books, quantities = 0
 *
 * Base Quantity = Quantity
 * Quote Quantity = Value/Total
 */
export async function calculateExecutionQuantities({
  amount,
  amountInQuote,
  marketData,
  orderbookState,
  stepSizeDecimals,
  limitPrice,
}: {
  amount: string;
  amountInQuote: boolean;
  marketData: Pick<ApiMarket, 'initialMarginFraction'>;
  /** Optional orderbook state if introspection is needed (it's not needed for Limit stop orders) */
  orderbookState?: {
    bids: ApiLevelTwoOrderBookRow[];
    asks: ApiLevelTwoOrderBookRow[];
  };
  stepSizeDecimals: number;
  limitPrice?: string;
}) {
  console.debug(
    `Calculating: amount=${amount} ${
      amountInQuote && '(In Quote)'
    }, initialMarginFraction=${
      marketData.initialMarginFraction
    }, limitPrice=${limitPrice}`
  );

  const buyEstimates = calculateImpact(
    amount,
    amountInQuote,
    limitPrice,
    stepSizeDecimals,
    orderbookState?.asks || [],
    'buy'
  );
  console.debug(
    `BUY: baseQuantity=${buyEstimates.baseQuantityFormatted}, quoteQuantity=${buyEstimates.quoteQuantityFormatted}, price=${buyEstimates.impactPrice}`
  );

  const sellEstimates = calculateImpact(
    amount,
    amountInQuote,
    limitPrice,
    stepSizeDecimals,
    orderbookState?.bids || [],
    'sell'
  );
  console.debug(
    `SELL: baseQuantity=${sellEstimates.baseQuantityFormatted}, quoteQuantity=${sellEstimates.quoteQuantityFormatted}, price=${sellEstimates.impactPrice}`
  );

  // TODO: BuySell: move correction to separate fn 2x ?
  // Correction for non-executed limit orders
  if (!amountInQuote && limitPrice) {
    const quantityBN = BigNumberJs(amount);
    const nonExecutedSellQuantity = quantityBN.minus(
      sellEstimates.baseQuantityBN
    );
    const nonExecutedBuyQuantity = quantityBN.minus(
      buyEstimates.baseQuantityBN
    );
    const nonExecutedSellQuoteQuantity =
      nonExecutedSellQuantity.multipliedBy(limitPrice);
    const nonExecutedBuyQuoteQuantity =
      nonExecutedBuyQuantity.multipliedBy(limitPrice);

    return {
      baseQuantityBuy: formatNumber(quantityBN, { thousandsSeparator: false }),
      baseQuantitySell: formatNumber(quantityBN, { thousandsSeparator: false }),
      quoteQuantityBuy: formatNumber(
        nonExecutedBuyQuoteQuantity.plus(buyEstimates.quoteQuantityFormatted),
        { thousandsSeparator: false }
      ),
      quoteQuantitySell: formatNumber(
        nonExecutedSellQuoteQuantity.plus(sellEstimates.quoteQuantityFormatted),
        { thousandsSeparator: false }
      ),
    };
  }

  // Correction for non-executed limit quote orders
  if (amountInQuote && limitPrice) {
    const valueBN = BigNumberJs(amount);
    const nonExecutedSellValue = valueBN.minus(
      sellEstimates.quoteQuantityFormatted
    );
    const nonExecutedBuyValue = valueBN.minus(
      buyEstimates.quoteQuantityFormatted
    );
    const nonExecutedSellQuantity = nonExecutedSellValue.dividedBy(limitPrice);
    const nonExecutedBuyQuantity = nonExecutedBuyValue.dividedBy(limitPrice);
    const quantityBuyBN = nonExecutedBuyQuantity.plus(
      buyEstimates.baseQuantityBN
    );
    const quantitySellBN = nonExecutedSellQuantity.plus(
      sellEstimates.baseQuantityBN
    );
    return {
      baseQuantityBuy: formatNumber(quantityBuyBN, {
        thousandsSeparator: false,
      }),
      baseQuantitySell: formatNumber(quantitySellBN, {
        thousandsSeparator: false,
      }),
      quoteQuantityBuy: formatNumber(valueBN, { thousandsSeparator: false }),
      quoteQuantitySell: formatNumber(valueBN, { thousandsSeparator: false }),
    };
  }

  // Market orders
  return {
    baseQuantityBuy: buyEstimates.baseQuantityFormatted,
    baseQuantitySell: sellEstimates.baseQuantityFormatted,
    quoteQuantityBuy: buyEstimates.quoteQuantityFormatted,
    quoteQuantitySell: sellEstimates.quoteQuantityFormatted,
  };
}

/**
 * Market orders = max position quantity
 * Limit orders = up to max position quantity - SDK calc
 * Stop orders = unlimited on frontend (API check for 2x max position market size)
 *
 * Full Spec https://docs.google.com/document/d/110xoSqKHZZYE03nTOXAfsjgnz-WIzA3Q5xnaZNtwBnM/edit
 * */
export function calculateReduceOnlyRestrictions({
  limitPrice,
  triggerEnabled,
  reduceOnly,
  openPosition,
  openOrders,
}: {
  /** False for-non limit orders */
  limitPrice: string | false;
  reduceOnly: boolean;
  triggerEnabled: boolean;
  /** False if no positions open */
  openPosition?: ApiPosition | false;
  openOrders: ApiOrder[] | undefined;
}) {
  if (!reduceOnly || openPosition === undefined || triggerEnabled) {
    return {
      maxBuyQuantity: false,
      maxSellQuantity: false,
    };
  }
  if (openPosition === false) {
    return { maxBuyQuantity: '0', maxSellQuantity: '0' };
  }
  if (!limitPrice) {
    const quantity = openPosition.quantity.replace('-', '');
    const isShort = openPosition.quantity.includes('-');

    return {
      maxBuyQuantity: isShort ? quantity : '0',
      maxSellQuantity: isShort ? '0' : quantity,
    };
  }

  if (!openOrders) {
    return { maxBuyQuantity: '0', maxSellQuantity: '0' };
  }

  // Spec https://docs.google.com/document/d/110xoSqKHZZYE03nTOXAfsjgnz-WIzA3Q5xnaZNtwBnM/
  // If sum of quantity of inside orders is less than open position quantity,
  // change the quantity of the incoming order to equal the difference and place it on the books
  // (i.e. use up 100% of the possible RO order quantity based on the open position)
  const determineMaxLimitQuantity = (orderSide: 'buy' | 'sell') => {
    return pipToDecimal(
      determineMaximumReduceOnlyQuantityAvailableAtPriceLevel({
        limitPrice: decimalToPip(limitPrice),
        orderSide,
        position: openPosition,
        walletsStandingOrders: openOrders,
      })
    );
  };
  return {
    maxBuyQuantity: determineMaxLimitQuantity('buy'),
    maxSellQuantity: determineMaxLimitQuantity('sell'),
  };
}

export const getOrderMinimum = (marketData: ApiMarket | undefined) =>
  Math.min(
    Number(marketData?.takerOrderMinimum),
    Number(marketData?.makerOrderMinimum)
  );

/** Dust orders are allowed in market reduce-only mode when existing position is below minimum */
export const isDustOrderAllowed = (
  panelState: PanelState,
  hasDustPosition: boolean
) =>
  panelState.reduceOnly && panelState.orderType === 'market' && hasDustPosition;

export const checkQuantityUnderMinimum = (
  panelState: PanelState,
  marketData: ApiMarket,
  hasDustPosition: boolean
) => {
  if (isDustOrderAllowed(panelState, hasDustPosition)) {
    return undefined;
  }
  const orderMinimum = getOrderMinimum(marketData);
  const sizeNumber = Number(panelState.size);
  return panelState.sizeInQuote ||
    panelState.sizePercentage ||
    !panelState.size ||
    !sizeNumber
    ? undefined
    : sizeNumber < orderMinimum;
};
